import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FounderNav } from '../components/FounderNav';
import {
  fetchCommunityMembers,
  fetchFounderCommunities,
  searchFounderUsers,
  setUserModerator,
  setUserStatus,
  type FounderCommunity,
  type FounderCommunityMember,
  type FounderUserSearchResult,
  type FounderUserStatus,
} from '../lib/api';
import { confirmDialog, promptDialog } from '../lib/dialog';

/** Short "3h ago" / "2d ago" for the sidebar's last-message hint. */
function timeAgo(iso: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return 'now';
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

/**
 * Founder People surface — the cross-community admin console.
 *
 * Two modes:
 *   1. Browse mode — pick a community, see its members, toggle
 *      moderator OR change status one-tap. Same as before.
 *   2. Search mode — type 2+ characters and the page flips to a
 *      cross-community search of every user (real name, email, or any
 *      membership nickname). Hits across communities show the user's
 *      memberships inline so a founder can act without first guessing
 *      which community they live in.
 *
 * Every mutation (moderator grant, suspend, ban, reinstate) writes an
 * audit_log row AND triggers the founder-write tripwire email — that
 * way a hijacked session shows up in the founders@ inbox in real time.
 */
export function FounderModerators() {
  const [communities, setCommunities] = useState<FounderCommunity[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [members, setMembers] = useState<FounderCommunityMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  // Search mode state — when query has 2+ chars, the right pane shows
  // search hits instead of the selected community's members.
  const [query, setQuery] = useState('');
  const [searchHits, setSearchHits] = useState<FounderUserSearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    fetchFounderCommunities().then((res) => {
      if (res.ok) setCommunities(res.data.communities);
      else if (res.stale) setStale(true);
      else setError(res.error);
    });
  }, []);

  const loadMembers = useCallback((communityId: string) => {
    setSelectedId(communityId);
    setMembers(null);
    setError(null);
    setQuery('');
    setSearchHits(null);
    fetchCommunityMembers(communityId).then((res) => {
      if (res.ok) setMembers(res.data.members);
      else if (res.stale) setStale(true);
      else setError(res.error);
    });
  }, []);

  // Debounce the cross-community search so a fast typer doesn't hammer
  // the endpoint. 280ms feels responsive without being twitchy.
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setSearchHits(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = window.setTimeout(() => {
      void searchFounderUsers(query).then((res) => {
        setSearching(false);
        if (res.ok) setSearchHits(res.data.users);
        else if (res.stale) setStale(true);
        else setError(res.error);
      });
    }, 280);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query]);

  /** Normalised shape the action handlers work with — bridges the
   *  community-member row (userId / userStatus) and the search-hit
   *  row (id / status) so the handlers don't need to switch on shape. */
  interface ActionTarget {
    userId: string;
    email: string;
    realName: string;
    status: string;
    isModerator: boolean;
    isFounder: boolean;
  }

  async function toggleModerator(target: ActionTarget, communityId: string) {
    if (pending) return;
    if (target.isFounder) {
      setError('That user is a founder. Founders are not also moderators.');
      return;
    }
    const next = !target.isModerator;
    const ok = await confirmDialog({
      title: `${next ? 'Grant' : 'Revoke'} moderator for ${target.realName || target.email}?`,
      message: next
        ? 'They will be able to see and act on flagged messages and audit logs in every community they are a member of.'
        : 'They will lose access to the moderation tools across every community.',
      confirmLabel: next ? 'Grant moderator' : 'Revoke moderator',
      danger: !next,
    });
    if (!ok) return;
    setPending(target.userId);
    setError(null);
    const result = await setUserModerator(target.userId, next, communityId);
    setPending(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    applyLocalChange(target.userId, { isModerator: next });
  }

  async function changeStatus(target: ActionTarget, next: FounderUserStatus) {
    if (pending) return;
    if (target.isFounder) {
      setError('That user is a founder. Revoke founder status first.');
      return;
    }
    if (target.status === next) return;
    const verb = next === 'active' ? 'Reinstate' : next === 'suspended' ? 'Suspend' : 'Ban';
    const note = await promptDialog({
      title: `${verb} ${target.realName || target.email}?`,
      message:
        next === 'active'
          ? 'Active conversations were ended when they were restricted — those stay ended.'
          : 'All of their active conversations will be ended immediately. They will not be matched until reinstated.',
      inputLabel: 'Audit-log note (optional)',
      placeholder: 'Why are you doing this?',
      multiline: true,
      confirmLabel: verb,
      danger: next !== 'active',
    });
    if (note === null) return; // cancel
    setPending(target.userId);
    setError(null);
    const result = await setUserStatus(target.userId, next, note || undefined);
    setPending(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    applyLocalChange(target.userId, { status: next, userStatus: next });
  }

  /** Optimistic patch into both local lists so the row reflects the
   *  change without a network round-trip. */
  function applyLocalChange(
    userId: string,
    patch: Partial<{
      isModerator: boolean;
      status: string;
      userStatus: string;
    }>,
  ) {
    setMembers(
      (prev) =>
        prev?.map((m) => {
          if (m.userId !== userId) return m;
          return {
            ...m,
            ...(patch.isModerator !== undefined && { isModerator: patch.isModerator }),
            ...(patch.userStatus !== undefined && { userStatus: patch.userStatus }),
          };
        }) ?? prev,
    );
    setSearchHits(
      (prev) =>
        prev?.map((u) => {
          if (u.id !== userId) return u;
          return {
            ...u,
            ...(patch.isModerator !== undefined && { isModerator: patch.isModerator }),
            ...(patch.status !== undefined && { status: patch.status }),
          };
        }) ?? prev,
    );
  }

  if (stale) {
    return (
      <main className="wide">
        <div className="card">
          <h1>Session is too old</h1>
          <p className="muted">
            Founder writes require a fresh sign-in. Reload from the home page and sign in again.
          </p>
          <p>
            <Link to="/">← Home</Link>
          </p>
        </div>
      </main>
    );
  }

  const selectedCommunity = communities?.find((c) => c.id === selectedId);
  const visibleMembers = members?.filter((m) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      m.realName.toLowerCase().includes(q) ||
      m.email.toLowerCase().includes(q) ||
      m.nickname.toLowerCase().includes(q)
    );
  });

  const inSearchMode = query.trim().length >= 2;

  return (
    <main className="wide">
      <div className="card">
        <FounderNav />
        <h1>People</h1>
        <p className="muted">
          Promote moderators or change a user's status. Every write is audit-logged and triggers a
          tripwire email to founders@.
        </p>

        {/* Cross-community search bar — always visible. Typing 2+ chars
            flips the right pane into search mode. */}
        <div className="founder-search">
          <input
            type="search"
            value={query}
            placeholder="Search every community by name, email, or nickname…"
            onChange={(e) => setQuery(e.target.value)}
            className="mod-filter founder-search-input"
            aria-label="Search across communities"
          />
          {query && (
            <button type="button" className="linklike" onClick={() => setQuery('')}>
              Clear
            </button>
          )}
        </div>

        {error && <p className="error">{error}</p>}

        <section className="moderators-layout">
          <aside className="moderators-side">
            <h2>Communities</h2>
            {!communities && <p className="muted">Loading…</p>}
            {communities && (
              <ul className="moderators-community-list">
                {communities.map((comm) => (
                  <li key={comm.id} className={comm.id === selectedId && !inSearchMode ? 'on' : ''}>
                    <button type="button" onClick={() => loadMembers(comm.id)}>
                      <span className="mod-comm-name">
                        {comm.name}
                        {comm.openModerationFlags > 0 && (
                          <span
                            className="mod-comm-flag"
                            title={`${comm.openModerationFlags} open moderation flag(s)`}
                          >
                            {comm.openModerationFlags}
                          </span>
                        )}
                      </span>
                      <span className="mod-comm-meta muted">
                        {comm.activeMembers} active · {comm.slug}
                        {comm.lastMessageAt && <> · last msg {timeAgo(comm.lastMessageAt)}</>}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <section className="moderators-main">
            {inSearchMode && (
              <>
                <header className="mod-header">
                  <h2>Search results for "{query}"</h2>
                </header>
                {searching && <p className="muted">Searching…</p>}
                {!searching && searchHits && searchHits.length === 0 && (
                  <p className="muted">No users match. Try a different fragment.</p>
                )}
                {!searching && searchHits && searchHits.length > 0 && (
                  <ul className="moderators-member-list">
                    {searchHits.map((u) => (
                      <li key={u.id} className="mod-row mod-row-tall">
                        <div className="mod-row-name">
                          <strong>{u.realName || '(no name)'}</strong>{' '}
                          {u.isFounder && (
                            <span className="status-pill status-active">founder</span>
                          )}
                          {u.isModerator && !u.isFounder && (
                            <span className="status-pill status-active">moderator</span>
                          )}
                          {u.status !== 'active' && (
                            <span className="status-pill status-suspended">{u.status}</span>
                          )}
                        </div>
                        <div className="mod-row-meta muted">{u.email}</div>
                        {u.memberships.length > 0 && (
                          <div className="mod-row-communities">
                            {u.memberships.map((m) => (
                              <span key={m.communityId} className="mod-comm-pill">
                                {m.communitySlug} · {m.nickname}
                              </span>
                            ))}
                          </div>
                        )}
                        <RowActions
                          member={{
                            id: u.id,
                            email: u.email,
                            realName: u.realName,
                            status: u.status,
                            isModerator: u.isModerator,
                            isFounder: u.isFounder,
                          }}
                          pending={pending === u.id}
                          onToggleModerator={() =>
                            void toggleModerator(
                              {
                                userId: u.id,
                                email: u.email,
                                realName: u.realName,
                                status: u.status,
                                isModerator: u.isModerator,
                                isFounder: u.isFounder,
                              },
                              u.memberships[0]?.communityId ?? '',
                            )
                          }
                          onChangeStatus={(next) =>
                            void changeStatus(
                              {
                                userId: u.id,
                                email: u.email,
                                realName: u.realName,
                                status: u.status,
                                isModerator: u.isModerator,
                                isFounder: u.isFounder,
                              },
                              next,
                            )
                          }
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            {!inSearchMode && !selectedCommunity && (
              <p className="muted">
                Pick a community to see its members, or search across all of them above.
              </p>
            )}
            {!inSearchMode && selectedCommunity && (
              <>
                <header className="mod-header">
                  <h2>{selectedCommunity.name}</h2>
                  <input
                    type="search"
                    value={filter}
                    placeholder="Filter this community…"
                    onChange={(e) => setFilter(e.target.value)}
                    className="mod-filter"
                  />
                </header>
                {!members && <p className="muted">Loading members…</p>}
                {members && members.length === 0 && <p className="muted">No members yet.</p>}
                {visibleMembers && visibleMembers.length > 0 && (
                  <ul className="moderators-member-list">
                    {visibleMembers.map((m) => (
                      <li key={m.userId} className="mod-row">
                        <div className="mod-row-name">
                          <strong>{m.realName || '(no name)'}</strong>{' '}
                          {m.isFounder && (
                            <span className="status-pill status-active">founder</span>
                          )}
                          {m.isModerator && !m.isFounder && (
                            <span className="status-pill status-active">moderator</span>
                          )}
                          {m.userStatus !== 'active' && (
                            <span className="status-pill status-suspended">{m.userStatus}</span>
                          )}
                        </div>
                        <div className="mod-row-meta muted">
                          {m.email} · nickname {m.nickname} · joined{' '}
                          {new Date(m.joinedAt).toLocaleDateString()}
                        </div>
                        <RowActions
                          member={{
                            id: m.userId,
                            email: m.email,
                            realName: m.realName,
                            status: m.userStatus,
                            isModerator: m.isModerator,
                            isFounder: m.isFounder,
                          }}
                          pending={pending === m.userId}
                          onToggleModerator={() =>
                            void toggleModerator(
                              {
                                userId: m.userId,
                                email: m.email,
                                realName: m.realName,
                                status: m.userStatus,
                                isModerator: m.isModerator,
                                isFounder: m.isFounder,
                              },
                              selectedCommunity.id,
                            )
                          }
                          onChangeStatus={(next) =>
                            void changeStatus(
                              {
                                userId: m.userId,
                                email: m.email,
                                realName: m.realName,
                                status: m.userStatus,
                                isModerator: m.isModerator,
                                isFounder: m.isFounder,
                              },
                              next,
                            )
                          }
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </section>
        </section>
      </div>
    </main>
  );
}

/** Per-row action group: moderator toggle + suspend/reinstate. Kept
 *  out of the main return so the search-mode and browse-mode lists
 *  can share the same button layout. */
function RowActions(props: {
  member: {
    id: string;
    email: string;
    realName: string;
    status: string;
    isModerator: boolean;
    isFounder: boolean;
  };
  pending: boolean;
  onToggleModerator: () => void;
  onChangeStatus: (next: FounderUserStatus) => void;
}) {
  const { member, pending, onToggleModerator, onChangeStatus } = props;
  if (member.isFounder) {
    return (
      <div className="mod-row-action">
        <span className="muted">protected</span>
      </div>
    );
  }
  const restricted = member.status === 'suspended' || member.status === 'banned';
  return (
    <div className="mod-row-action">
      <button
        type="button"
        disabled={pending}
        onClick={onToggleModerator}
        className={member.isModerator ? 'secondary' : ''}
      >
        {member.isModerator ? 'Revoke mod' : 'Make mod'}
      </button>
      {restricted ? (
        <button
          type="button"
          className="secondary"
          disabled={pending}
          onClick={() => onChangeStatus('active')}
        >
          Reinstate
        </button>
      ) : (
        <>
          <button
            type="button"
            className="secondary"
            disabled={pending}
            onClick={() => onChangeStatus('suspended')}
          >
            Suspend
          </button>
          <button
            type="button"
            className="secondary danger"
            disabled={pending}
            onClick={() => onChangeStatus('banned')}
          >
            Ban
          </button>
        </>
      )}
    </div>
  );
}
